import { Component, HostListener, Inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SIGNOUT_URL } from './app.constants';
import { AppService } from './services/app.service';
import { AuthService } from './services/auth.service';
import { DownloadService } from './services/download.service';
import { ActionTypes } from './store/actions/content.action';
import { ContentState } from './store/state/content.state';
import { AnalyticsUtil } from './utils/analytics.util';
import { LocalizationUtil } from './utils/localization.util';
import { Router, NavigationStart } from '@angular/router';
import { filter, takeWhile } from 'rxjs/operators';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

@Component({
    selector: 'u4-cpp',
    templateUrl: './app.component.html'
})
export class AppComponent {

    public isReady: Observable<boolean>;
    public initialized = false;

    constructor(
        private appService: AppService,
        private authService: AuthService,
        private localizationUtil: LocalizationUtil,
        private analyticsUtil: AnalyticsUtil,
        private store: Store<ContentState>,
        private router: Router,
        private downloadService: DownloadService,
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
    ) {
        this.isReady = this.appService.isReady;

        // The router-outlet lives inside the layout, which is not rendered until the app is
        // authorized + labels are loaded. During the Okta login redirect we are not yet
        // authorized, so no outlet exists to host a routed callback component — perform the
        // code→token exchange here at the app root, then restoreOriginalUri navigates onward.
        if (this.oktaAuth.isLoginRedirect()) {
            this.oktaAuth.handleLoginRedirect().catch((err) => {
                console.error('Okta login redirect handling failed', err);
                void this.router.navigate(['/news']);
            });
        }

        this.router.events.pipe(
            filter(e => e instanceof NavigationStart),
            takeWhile(() => this.initialized === false)
        ).subscribe((e: NavigationStart) => {
            // On the way out (SignoutGuard) the tokens are about to be revoked, so skip the boot
            // sequence: it would fire off content requests with a token that dies mid-flight.
            if (this.authService.isAuthorized() && e.url.startsWith(`/${SIGNOUT_URL}`) === false) {
                this.init();
            }
        });
    }

    /**
     * Secured file-share links pasted into CMS rich text arrive as plain anchors inside
     * `[innerHTML]`, so clicking one is a top-level navigation with no bearer token and the backend
     * answers 401. Listening at the app root catches them wherever they were authored — news
     * bodies, accordions, notifications, the footer — without every CMS-rendered container having
     * to opt in. DownloadService decides whether the click is one of ours; everything else falls
     * through to the browser untouched.
     * @param {MouseEvent} event the click, captured as it bubbles to the document
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        this.downloadService.interceptLinkClick(event);
    }

    init() {
        this.localizationUtil.init();
        console.log('Analytics initialized');
        void this.analyticsUtil.init();
        this.store.dispatch({type: ActionTypes.GetLabels});
        this.initialized = true;
    }

}
